import { Component } from 'react';
import PropTypes from 'prop-types';

// Error boundaries must be class components -- there's no hook equivalent of
// getDerivedStateFromError/componentDidCatch as of React 18.

const RELOAD_FLAG_KEY = 'chunk-error-reload-attempted';

// Every page in this app is lazy-loaded with a content-hashed chunk filename. When a browser
// tab stays open across a new deploy, its already-loaded main.js still references the old
// (now-deleted) chunk filenames -- the server's SPA fallback returns index.html for that missing
// path instead of a 404, the browser tries to run that HTML as JS, and the lazy import throws
// with nothing in the app catching it, unmounting the whole tree to a blank page. A single
// reload fetches the current build and resolves the mismatch.
const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' || /Loading chunk [\d]+ failed/.test(error?.message || '') || /Unexpected token '<'/.test(error?.message || '');

class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // Guarded by a one-shot sessionStorage flag so a genuinely broken chunk (not just a stale
    // deploy) doesn't reload-loop forever -- sessionStorage is cleared when the tab closes, so
    // a later, separate session gets a fresh attempt.
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_FLAG_KEY)) {
      sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      // Chunk-load errors reload automatically above; this only shows if that reload already
      // happened once and the error recurred, or for any other unexpected render error -- a
      // blank screen with no explanation is worse than this.
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: 16,
            textAlign: 'center',
            padding: 16
          }}
        >
          <p>Something went wrong loading this page.</p>
          <button type="button" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

ChunkErrorBoundary.propTypes = {
  children: PropTypes.node
};

export default ChunkErrorBoundary;
